import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';

/**
 * AvatarJourney - Magical transition animation showing the SEO consultant 
 * "following you" from the main chat into tutorial mode
 * 
 * This creates the illusion that your trusted consultant is moving with you
 * rather than disappearing and reappearing in a different interface.
 */
const AvatarJourney = ({ 
  isActive = false, 
  onComplete = () => {},
  darkMode = false 
}) => {
  const [startPosition, setStartPosition] = useState(null);
  const [endPosition, setEndPosition] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);

  // Calculate positions when animation becomes active
  useEffect(() => {
    if (isActive && !isAnimating) {
      calculatePositions();
    }
  }, [isActive]);

  const calculatePositions = () => {
    // Find the main chat sidebar avatar (in ChatSidebarHeader)
    const mainChatHeader = document.querySelector('[data-chat-header]');
    const mainAvatar = mainChatHeader?.querySelector('[data-avatar]');
    
    if (mainAvatar) {
      const rect = mainAvatar.getBoundingClientRect();
      setStartPosition({
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2,
        width: rect.width,
        height: rect.height
      });
    } else {
      // Fallback position if we can't find the main avatar
      setStartPosition({
        x: window.innerWidth - 200, // Approximate chat sidebar position
        y: 100,
        width: 32,
        height: 32
      });
    }

    // Calculate end position (center of screen, where tutorial will open)
    setEndPosition({
      x: window.innerWidth / 2,
      y: window.innerHeight / 2,
      width: 48, // Slightly larger in tutorial
      height: 48
    });

    // Start animation after positions are calculated
    setTimeout(() => {
      setIsAnimating(true);
    }, 100);
  };

  const handleAnimationComplete = () => {
    setIsAnimating(false);
    onComplete();
  };

  if (!isActive || !startPosition || !endPosition) return null;

  const journeyContent = (
    <AnimatePresence>
      {isAnimating && (
        <>
          {/* Sparkle trail background */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 pointer-events-none z-[10001]"
          >
            {/* Sparkle particles along the path */}
            {[...Array(8)].map((_, i) => (
              <motion.div
                key={i}
                initial={{
                  x: startPosition.x,
                  y: startPosition.y,
                  scale: 0,
                  opacity: 0
                }}
                animate={{
                  x: startPosition.x + (endPosition.x - startPosition.x) * (i * 0.15),
                  y: startPosition.y + (endPosition.y - startPosition.y) * (i * 0.15),
                  scale: [0, 1, 0],
                  opacity: [0, 1, 0]
                }}
                transition={{
                  duration: 2,
                  delay: i * 0.1,
                  ease: "easeInOut"
                }}
                className="absolute w-2 h-2 bg-purple-400 rounded-full"
                style={{
                  filter: 'blur(0.5px)',
                  boxShadow: '0 0 6px rgba(168, 85, 247, 0.8)'
                }}
              />
            ))}
          </motion.div>

          {/* Main avatar journey */}
          <motion.div
            initial={{
              x: startPosition.x - startPosition.width / 2,
              y: startPosition.y - startPosition.height / 2,
              scale: 1,
              opacity: 1
            }}
            animate={{
              x: endPosition.x - endPosition.width / 2,
              y: endPosition.y - endPosition.height / 2,
              scale: 1.2,
              opacity: 1
            }}
            exit={{
              scale: 0,
              opacity: 0
            }}
            transition={{
              duration: 1.5,
              ease: [0.25, 0.46, 0.45, 0.94], // Custom easing for smooth journey
            }}
            onAnimationComplete={handleAnimationComplete}
            className="fixed z-[10002] pointer-events-none"
            style={{
              width: endPosition.width,
              height: endPosition.height
            }}
          >
            {/* Glowing avatar container */}
            <div className="relative w-full h-full">
              {/* Glow effect */}
              <motion.div
                animate={{
                  scale: [1, 1.3, 1],
                  opacity: [0.3, 0.6, 0.3]
                }}
                transition={{
                  duration: 1,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                className="absolute inset-0 bg-purple-500 rounded-lg blur-md"
              />
              
              {/* Main avatar - exact copy of ChatSidebarHeader avatar */}
              <div className="relative w-full h-full bg-white bg-opacity-20 rounded-lg flex items-center justify-center backdrop-blur-sm border-2 border-purple-400 shadow-lg">
                <motion.svg 
                  className="w-6 h-6 text-white" 
                  fill="currentColor" 
                  viewBox="0 0 20 20"
                  animate={{
                    rotate: [0, 360],
                  }}
                  transition={{
                    duration: 1.5,
                    ease: "easeInOut"
                  }}
                >
                  <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd" />
                </motion.svg>
              </div>

              {/* Trailing message bubble */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0 }}
                transition={{ delay: 0.3, duration: 0.5 }}
                className={`
                  absolute -bottom-8 left-1/2 transform -translate-x-1/2 whitespace-nowrap
                  px-3 py-1 rounded-full text-xs font-medium
                  ${darkMode 
                    ? 'bg-gray-800 text-purple-300 border border-purple-600' 
                    : 'bg-white text-purple-700 border border-purple-300'
                  }
                  shadow-lg
                `}
              >
                Following you...
              </motion.div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  return createPortal(journeyContent, document.body);
};

export default AvatarJourney;

